import { normalizeTier, type PlanTier } from '@/lib/planLimits';
import { mapPriceIdToTier, isPaidStripeSubscriptionStatus } from '@/lib/stripe/price-tier-map';
import type { BillingIdentityLink } from '@/lib/billing/billing-identity';
import type { BillingProfileFields } from '@/lib/billing/paid-access';
import { resolvePlanTierFromBillingIdentity } from '@/lib/billing/resolve-plan';

export type EntitlementSource =
  | 'user_profiles'
  | 'billing_identity_links'
  | 'stripe_sync'
  | 'free_default';

export type AccountType = 'individual' | 'institution';

export type EntitlementProfileFields = BillingProfileFields & {
  role?: string | null;
  subscription_current_period_end?: string | null;
  email?: string | null;
  login_email?: string | null;
};

export type ResolvedEntitlement = {
  plan: PlanTier;
  tier: PlanTier;
  accountType: AccountType;
  status: string;
  priceId: string | null;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  currentPeriodEnd: string | null;
  source: EntitlementSource;
  needsSync: boolean;
  error?: string;
};

export function isPaidSubscriptionStatus(status: string | null | undefined): boolean {
  return isPaidStripeSubscriptionStatus(status);
}

export function isPaidPlanTier(tier: PlanTier): boolean {
  return tier !== 'free' && tier !== 'collective_free';
}

export function accountTypeFromProfile(
  profile: EntitlementProfileFields | null | undefined
): AccountType {
  return profile?.role === 'institution' ? 'institution' : 'individual';
}

/** Derive tier from user_profiles billing fields. Unknown price_id never forces free when status is paid. */
export function tierFromProfileFields(
  profile: EntitlementProfileFields | null | undefined
): PlanTier {
  if (!profile) return 'free';

  const priceId = (profile.price_id || '').trim();
  if (priceId) {
    const mapped = mapPriceIdToTier(priceId);
    if (mapped) return mapped;
    if (isPaidSubscriptionStatus(profile.subscription_status)) {
      const fromTier = normalizeTier(profile.subscription_tier ?? profile.plan);
      if (fromTier !== 'free') return fromTier;
    }
    return 'free';
  }

  // subscription_tier is the current normalized entitlement field. `plan` is
  // retained for legacy compatibility and may still contain obsolete values.
  const fromPlan = normalizeTier(profile.subscription_tier ?? profile.plan);
  return fromPlan === 'free' ? 'free' : fromPlan;
}

/**
 * True when user_profiles alone grants paid access (authoritative).
 * Paid status + paid tier — does not require billing_identity_links.
 */
export function isAuthoritativeProfileEntitled(
  profile: EntitlementProfileFields | null | undefined
): boolean {
  if (!profile) return false;
  if (!isPaidSubscriptionStatus(profile.subscription_status)) return false;
  const tier = tierFromProfileFields(profile);
  return isPaidPlanTier(tier);
}

function profileHasUnknownPriceId(profile: EntitlementProfileFields | null | undefined): boolean {
  const priceId = (profile?.price_id || '').trim();
  if (!priceId) return false;
  return !mapPriceIdToTier(priceId);
}

/**
 * Single entitlement resolver for the entire app.
 *
 * Priority:
 * A. Paid user_profiles (subscription_status active/trialing/past_due + paid tier)
 * B. billing_identity_links only when profile is not paid
 * C. free_default
 *
 * Stale or broken billing_identity_links must NEVER downgrade a paid user_profiles row.
 */
export function resolveEntitlement(
  profile: EntitlementProfileFields | null | undefined,
  billingIdentity?: BillingIdentityLink | null | undefined
): ResolvedEntitlement {
  const accountType = accountTypeFromProfile(profile);
  const profileTier = tierFromProfileFields(profile);
  const billingIdentityTier = resolvePlanTierFromBillingIdentity(billingIdentity);
  const base = {
    accountType,
    priceId: (profile?.price_id || billingIdentity?.price_id || '').trim() || null,
    stripeCustomerId:
      (profile?.stripe_customer_id || billingIdentity?.stripe_customer_id || '').trim() || null,
    stripeSubscriptionId:
      (profile?.stripe_subscription_id || billingIdentity?.stripe_subscription_id || '').trim() ||
      null,
    currentPeriodEnd:
      profile?.subscription_current_period_end || billingIdentity?.current_period_end || null,
    needsSync: false,
  };

  // `plus` is an obsolete, non-canonical profile value. When it remains on a
  // migrated individual account, defer to the active billing identity rather
  // than allowing another stale profile field to override the current plan.
  if (
    accountType === 'individual' &&
    profile?.plan?.trim().toLowerCase() === 'plus' &&
    billingIdentityTier !== 'free'
  ) {
    return {
      ...base,
      plan: billingIdentityTier,
      tier: billingIdentityTier,
      status: (billingIdentity?.status || 'active').toLowerCase(),
      source: 'billing_identity_links',
      priceId: (billingIdentity?.price_id || profile.price_id || '').trim() || null,
      stripeCustomerId:
        (billingIdentity?.stripe_customer_id || profile.stripe_customer_id || '').trim() || null,
      stripeSubscriptionId:
        (billingIdentity?.stripe_subscription_id || profile.stripe_subscription_id || '').trim() ||
        null,
      currentPeriodEnd:
        billingIdentity?.current_period_end || profile.subscription_current_period_end || null,
    };
  }

  if (profile && isAuthoritativeProfileEntitled(profile)) {
    const tier = profileTier;
    const unknownPrice = profileHasUnknownPriceId(profile);
    return {
      ...base,
      plan: tier,
      tier,
      status: (profile.subscription_status || 'active').toLowerCase(),
      source: 'user_profiles',
      needsSync: unknownPrice,
      error: unknownPrice
        ? `Unknown Stripe price_id ${profile.price_id} — using subscription_tier fallback`
        : undefined,
    };
  }

  if (
    profile &&
    isPaidSubscriptionStatus(profile.subscription_status) &&
    profileHasUnknownPriceId(profile)
  ) {
    const fallbackTier = normalizeTier(profile.subscription_tier ?? profile.plan);
    if (fallbackTier !== 'free') {
      return {
        ...base,
        plan: fallbackTier,
        tier: fallbackTier,
        status: (profile.subscription_status || 'active').toLowerCase(),
        source: 'user_profiles',
        needsSync: true,
        error: `Unknown Stripe price_id ${profile.price_id}`,
      };
    }
  }

  if (billingIdentity?.user_id) {
    if (billingIdentityTier !== 'free') {
      return {
        ...base,
        plan: billingIdentityTier,
        tier: billingIdentityTier,
        status: (billingIdentity.status || 'active').toLowerCase(),
        source: 'billing_identity_links',
        priceId: base.priceId || (billingIdentity.price_id || '').trim() || null,
        stripeCustomerId:
          base.stripeCustomerId || (billingIdentity.stripe_customer_id || '').trim() || null,
        stripeSubscriptionId:
          base.stripeSubscriptionId ||
          (billingIdentity.stripe_subscription_id || '').trim() ||
          null,
        currentPeriodEnd: base.currentPeriodEnd || billingIdentity.current_period_end || null,
      };
    }
  }

  // Collective accounts retain the Collective Free baseline when a paid
  // collective subscription ends. Keep this after the active billing-link
  // fallback so a delayed profile sync cannot downgrade an active paid link.
  if (
    profile &&
    (profileTier === 'collective_free' ||
      (accountType === 'institution' && profileTier.startsWith('collective_')))
  ) {
    return {
      ...base,
      plan: 'collective_free',
      tier: 'collective_free',
      status: (profile.subscription_status || 'free').toLowerCase(),
      source: 'user_profiles',
    };
  }

  return {
    ...base,
    plan: 'free',
    tier: 'free',
    status: 'free',
    source: 'free_default',
  };
}

export function entitlementToPlanTier(entitlement: ResolvedEntitlement): PlanTier {
  return entitlement.tier;
}

/** Structured server log for entitlement resolution (requirement: log source). */
export function logEntitlementResolution(
  context: string,
  payload: {
    userId?: string | null;
    entitlement: ResolvedEntitlement;
    profileTier?: string | null;
    linkPlan?: string | null;
    linkStatus?: string | null;
  }
): void {
  console.log('[ErtzyxEntitlement]', {
    context,
    userId: payload.userId ?? null,
    source: payload.entitlement.source,
    tier: payload.entitlement.tier,
    status: payload.entitlement.status,
    priceId: payload.entitlement.priceId,
    stripeCustomerId: payload.entitlement.stripeCustomerId,
    stripeSubscriptionId: payload.entitlement.stripeSubscriptionId,
    needsSync: payload.entitlement.needsSync,
    error: payload.entitlement.error ?? null,
    profileTier: payload.profileTier ?? null,
    linkPlan: payload.linkPlan ?? null,
    linkStatus: payload.linkStatus ?? null,
  });
}
